import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionTitle from '@/components/base/SectionTitle';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getNewsById, getRelatedNews } from '@/utils/media';
import { newsCategoryLabels } from '@/mocks/news';

export default function NewsDetailPage() {
  const { id = '' } = useParams();
  const item = getNewsById(id);

  useDocumentMeta(
    item ? `【${item.title}】- 资讯详情 - 黄牛视频` : '资讯不存在 - 黄牛视频',
    item ? item.subtitle : '该资讯内容不存在或已删除，可返回资讯列表浏览其他内容。',
    item ? `${item.title},影视资讯,黄牛视频` : '黄牛视频,影视资讯',
  );

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="资讯内容不存在"
          description="该资讯可能已删除或链接失效，返回资讯列表查看更多影视动态。"
          actionLabel="返回资讯列表"
          actionTo="/news"
        />
      </div>
    );
  }

  const related = getRelatedNews(item, 3);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '影视资讯', to: '/news' },
          { label: item.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <span className="inline-block rounded bg-secondary-500/20 px-2.5 py-1 text-xs text-secondary-300">
            {newsCategoryLabels[item.category]}
          </span>
          <h1 className="mt-3 text-2xl font-bold leading-snug text-foreground-50 md:text-3xl">
            {item.title}
          </h1>
          <p className="mt-3 text-sm text-foreground-500">发布时间：{item.publishedAt}</p>

          <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} 配图`}
              className="aspect-video h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-5">
            {item.content.map((p, idx) => (
              <p key={idx} className="text-sm leading-loose text-foreground-300 md:text-base">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-between rounded-lg border border-background-200 bg-background-100 p-4">
            <Link
              to="/news"
              className="flex items-center gap-1 text-sm text-foreground-400 transition-colors hover:text-primary-500"
            >
              <i className="ri-arrow-left-s-line text-lg" />
              返回资讯列表
            </Link>
            <span className="text-xs text-foreground-600">
              © 黄牛视频 · 内容仅供学习交流
            </span>
          </div>
        </article>

        <aside>
          <SectionTitle title="相关文章" icon="ri-links-line" accent="accent" />
          <div className="space-y-4">
            {related.map((rel) => (
              <Link
                key={rel.id}
                to={`/news/${rel.id}`}
                className="group block rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-400/60"
              >
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="rounded bg-secondary-500/20 px-2 py-0.5 text-secondary-300">
                    {newsCategoryLabels[rel.category]}
                  </span>
                  <span>{rel.publishedAt}</span>
                </div>
                <h3 className="mt-2 line-clamp-2 text-sm font-semibold leading-snug text-foreground-100 transition-colors group-hover:text-primary-500">
                  {rel.title}
                </h3>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}